import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Icon,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  VersionTimeline,
  cn,
  type VersionSourceTone,
} from "@nexus/ui";
import { splitVersionChangeLabel, VERSION_CHANGE_LABEL_SEPARATOR } from "@nexus/contracts";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { formatCompactLocalDateTime } from "@/features/opportunities/lib/format-relative-time.js";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { getPersonaScriptDisplayTitle } from "@/features/persona-opportunities/lib/script-display-title";
import {
  personaScriptVersionHistoryQueryOptions,
  personaScriptVersionQueryOptions,
  useRestorePersonaScriptVersion,
} from "@/lib/queries/persona-opportunity-scripts.queries";

import { PersonaScriptFieldDiff } from "./PersonaScriptFieldDiff.js";
import {
  PERSONA_SCRIPT_SCENE_SCROLL_FOCUS_CLASS,
  personaScriptSceneRowClass,
  personaScriptSceneTableLayout,
} from "./PersonaScriptSceneTable.js";
import {
  CollapsedVersionsRail,
  OPEN_RAIL_CLASS,
  OPEN_RAIL_CONTENT_CLASS,
  VersionsRailCollapseButton,
  useVersionsRail,
  versionsRailGridClass,
} from "./VersionsRail.js";
import {
  highlightedSceneClass,
  personaScriptSceneFieldDiffersFromOriginal,
  sceneFieldBlameByKey,
  sceneFieldBlameKey,
  type PersonaScriptHighlightField,
} from "./persona-script-version-highlight.js";

const SOURCE_I18N: Record<string, { key: string; fallback: string }> = {
  GENERATED: { key: "personaOpportunities.scripts.history.sourceGenerated", fallback: "Generated" },
  MANUAL: { key: "personaOpportunities.scripts.history.sourceManual", fallback: "Manual" },
  AI_EDIT: { key: "personaOpportunities.scripts.history.sourceAiEdit", fallback: "AI edit" },
  RESTORED: { key: "personaOpportunities.scripts.history.sourceRestored", fallback: "Restored" },
};

const SOURCE_TONE: Record<string, VersionSourceTone> = {
  GENERATED: "generated",
  MANUAL: "manual",
  AI_EDIT: "ai",
  RESTORED: "restored",
};

/** Stored write-time labels; "Hook" is the pre-Title name for a title edit. */
const CHANGE_LABEL_I18N: Record<string, { key: string; fallback: string }> = {
  Title: { key: "personaOpportunities.scripts.history.title", fallback: "Title" },
  Hook: { key: "personaOpportunities.scripts.history.title", fallback: "Title" },
};

function translateScriptChangeLabel(label: string | null, t: TFunction): string | null {
  const parts = splitVersionChangeLabel(label);
  if (parts.length === 0) return null;
  return parts
    .map((part) => {
      const copy = CHANGE_LABEL_I18N[part];
      return copy ? t(copy.key, copy.fallback) : part;
    })
    .join(VERSION_CHANGE_LABEL_SEPARATOR);
}

const SCENE_FIELDS = [
  { field: "seeing" as const, key: "personaOpportunities.scripts.history.seeing", fallback: "Seeing" },
  { field: "hearing" as const, key: "personaOpportunities.scripts.history.hearing", fallback: "Hearing" },
  { field: "product" as const, key: "personaOpportunities.scripts.history.product", fallback: "Product" },
  { field: "props" as const, key: "personaOpportunities.scripts.history.props", fallback: "Props" },
];

const SCENE_GRID_COLUMNS = 2;

export type ScriptHistorySurface = "card" | "drawer";

export interface ScriptHistoryView {
  isLatest: boolean;
  preview: PersonaScriptDto | undefined;
  selectedVersion: number;
  versionCount: number;
  title: string;
  concept: string | null | undefined;
  /**
   * When this version's artifact was written. Null for a legacy row with no
   * `PersonaScriptVersion` (`id` null): that `createdAt` is the earliest scene
   * time or the live script birth, not a version write.
   */
  versionCreatedAt: string | null;
  /** v1, which scene cells are highlighted against. */
  original: PersonaScriptDto | undefined;
}

export interface ScriptsHistoryPanelProps {
  enabled: boolean;
  setId: string;
  tenantId: string;
  script: PersonaScriptDto;
  canRestore: boolean;
  surface?: ScriptHistorySurface;
  /** Hub drawer omits the italic concept under "Viewing" on the current version. */
  hideCurrentConcept?: boolean;
  /** Sheet chrome above the two-column body. */
  header?: (view: ScriptHistoryView) => ReactNode;
  /** Lifts the selection for chrome outside the panel; `null` follows the latest version. */
  selection?: { version: number | null; onChange: (version: number | null) => void };
  /** Content above the "Viewing" line, e.g. the hub drawer's title and metadata. */
  intro?: (view: ScriptHistoryView) => ReactNode;
  /**
   * Hub embed. `scenePreview` is the older version or the diff, and is null on the latest
   * version in flat mode; the embed renders it where its own scene table goes.
   */
  leading?: (view: ScriptHistoryView, scenePreview: ReactNode) => ReactNode;
}

export interface ScriptsHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  script: PersonaScriptDto;
  canRestore: boolean;
}

export function ScriptsHistoryPanel({
  enabled,
  setId,
  tenantId,
  script,
  canRestore,
  surface = "card",
  hideCurrentConcept = false,
  header,
  selection,
  intro,
  leading,
}: ScriptsHistoryPanelProps) {
  const { t } = useTranslation();
  /** `null` follows the latest version, so a save or restore lands on the new one. */
  const [localSelectedVersion, setLocalSelectedVersion] = useState<number | null>(null);
  const selectedVersion = selection ? selection.version : localSelectedVersion;
  const setSelectedVersion = selection ? selection.onChange : setLocalSelectedVersion;
  /** Version whose card has "Show differences" on; a new latest version lands flat. */
  const [diffVersion, setDiffVersion] = useState<number | null>(null);
  const restore = useRestorePersonaScriptVersion();
  const restoreInFlight = useRef(false);

  const historyQuery = useQuery(
    personaScriptVersionHistoryQueryOptions(tenantId, setId, script.id, { enabled }),
  );
  const versions = historyQuery.data?.versions ?? [];
  // The saved script lands in the cache before the history refetch does.
  const latestVersion = Math.max(historyQuery.data?.activeVersion ?? 0, script.versionCount);
  const viewingVersion = selectedVersion ?? latestVersion;
  const isLatest = viewingVersion === latestVersion;

  useEffect(() => {
    if (!enabled) {
      setSelectedVersion(null);
      setDiffVersion(null);
    }
  }, [enabled, setSelectedVersion]);

  function selectVersion(version: number) {
    setSelectedVersion(version === latestVersion ? null : version);
    setDiffVersion(null);
  }

  const previewQuery = useQuery(
    personaScriptVersionQueryOptions(
      tenantId,
      setId,
      script.id,
      viewingVersion > 0 ? viewingVersion : null,
      { enabled: enabled && viewingVersion > 0 },
    ),
  );
  const originalQuery = useQuery(
    personaScriptVersionQueryOptions(tenantId, setId, script.id, 1, {
      enabled,
    }),
  );
  const previousVersion = viewingVersion > 1 ? viewingVersion - 1 : null;
  const previousQuery = useQuery(
    personaScriptVersionQueryOptions(tenantId, setId, script.id, previousVersion, {
      enabled: enabled && previousVersion != null,
    }),
  );

  const preview = previewQuery.isPlaceholderData ? undefined : previewQuery.data?.script;
  const original = originalQuery.data?.script;
  const previous =
    previousVersion != null &&
    !previewQuery.isPlaceholderData &&
    !previousQuery.isPlaceholderData
      ? previousQuery.data?.script
      : undefined;
  const untitled = t("personaOpportunities.scripts.untitledHook", {
    defaultValue: "Script {{slot}}",
    slot: script.slotIndex,
  });
  const title = getPersonaScriptDisplayTitle(preview ?? script, untitled);
  const concept = preview?.concept;
  const selectedInstruction =
    versions.find((entry) => entry.version === viewingVersion)?.instruction ?? null;
  const versionCount = versions.length || script.versionCount;
  const blameByKey = useMemo(
    () => sceneFieldBlameByKey(versions, viewingVersion),
    [versions, viewingVersion],
  );

  const timelineVersions = useMemo(
    () =>
      versions.map((entry) => ({
        id: entry.id ?? `v${entry.version}`,
        version: entry.version,
        createdAt: entry.createdAt,
        createdAtLabel: formatCompactLocalDateTime(entry.createdAt, Date.now()),
        createdBy:
          entry.createdBy === "creative_engine"
            ? t("personaOpportunities.scripts.history.creativeEngine", "Creative engine")
            : entry.createdBy,
        sourceBadge: SOURCE_I18N[entry.source]
          ? t(SOURCE_I18N[entry.source].key, SOURCE_I18N[entry.source].fallback)
          : entry.source,
        sourceTone: SOURCE_TONE[entry.source],
        changeLabel: translateScriptChangeLabel(entry.changeLabel, t),
        detail: entry.instruction,
        isCurrentState: entry.version === latestVersion,
      })),
    [latestVersion, t, versions],
  );

  const activeVersionId =
    versions.find((entry) => entry.version === viewingVersion)?.id ?? `v${viewingVersion}`;

  function handleRestore() {
    if (selectedVersion == null || isLatest || restore.isPending || restoreInFlight.current) {
      return;
    }
    restoreInFlight.current = true;
    restore.mutate(
      { setId, scriptId: script.id, version: selectedVersion, tenantId },
      {
        onSuccess: () => {
          setSelectedVersion(null);
        },
        onSettled: () => {
          restoreInFlight.current = false;
        },
      },
    );
  }

  const drawer = surface === "drawer";
  const rail = useVersionsRail(drawer, versionCount);
  const canShowChanges = previousVersion != null;
  const isShowingChanges = canShowChanges && diffVersion === viewingVersion;
  const viewedEntry = versions.find((entry) => entry.version === viewingVersion);
  const view: ScriptHistoryView = {
    isLatest,
    preview,
    selectedVersion: viewingVersion,
    versionCount,
    title,
    concept,
    versionCreatedAt: viewedEntry?.id != null ? viewedEntry.createdAt : null,
    original,
  };
  const showPreview = leading == null || !isLatest || isShowingChanges;
  const scenePreview = !showPreview ? null : !preview ? (
    <div
      className="flex flex-col gap-3"
      aria-label={t("personaOpportunities.scripts.history.loadingPreview", "Loading preview…")}
    >
      <Skeleton className="h-36 w-full rounded-sm" />
      <Skeleton className="h-36 w-full rounded-sm" />
    </div>
  ) : (
    <HistoryPreview
      surface={surface}
      script={preview}
      original={original}
      previous={isShowingChanges ? previous : undefined}
      blameByKey={blameByKey}
      onSelectVersion={selectVersion}
    />
  );
  const showConcept = Boolean(concept) && !(hideCurrentConcept && isLatest);
  const showDifferencesLabel = t(
    "personaOpportunities.scripts.history.showDifferences",
    "Show differences",
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header?.(view)}
      <div className={versionsRailGridClass(drawer, rail)}>
          <div
            className={cn(
              "min-h-0 overflow-y-auto px-6 py-5",
              !rail.isHidden && "border-b border-border md:border-b-0 md:border-r",
            )}
          >
            {intro?.(view)}
            <div className="mb-3.5 flex min-h-8 items-start justify-between gap-3.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className={drawer ? "text-drawer-body font-medium" : "text-xs font-semibold"}>
                    {t("personaOpportunities.scripts.history.viewing", {
                      defaultValue: "Viewing v{{version}}",
                      version: viewingVersion,
                    })}
                  </p>
                  {isLatest && !rail.isHidden ? (
                    <span
                      className={
                        drawer
                          ? "rounded-full bg-ai-subtle px-2 py-px text-caption font-medium text-ai-subtle-foreground"
                          : "rounded-full bg-ai-subtle px-2 py-px text-[10px] font-medium text-ai-subtle-foreground"
                      }
                    >
                      {t("personaOpportunities.scripts.history.current", "Current")}
                    </span>
                  ) : null}
                </div>
                {showConcept ? (
                  <p className="mt-[3px] text-xs text-muted-foreground italic">{concept}</p>
                ) : null}
                {isShowingChanges &&
                preview &&
                previous &&
                previewDiffersFromPrevious(preview, previous) ? (
                  <p className={drawer ? "mt-1 text-caption text-muted-foreground" : "mt-1 text-[11px] text-muted-foreground"}>
                    {t(
                      "personaOpportunities.scripts.history.diffLegend",
                      "Struck text was removed. Underlined text is the new wording.",
                    )}
                  </p>
                ) : null}
                {selectedInstruction ? (
                  <div className="mt-2.5">
                    <p
                      className={
                        drawer
                          ? "mb-1 text-caption font-medium tracking-label text-muted-foreground uppercase"
                          : "mb-1 font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                      }
                    >
                      {t("personaOpportunities.scripts.history.prompt", "Prompt")}
                    </p>
                    <p
                      className={
                        drawer
                          ? "text-drawer-body leading-relaxed break-words whitespace-pre-wrap text-ai-text italic"
                          : "text-xs italic leading-relaxed break-words whitespace-pre-wrap text-ai-text"
                      }
                    >
                      {t("personaOpportunities.scripts.history.promptQuote", {
                        defaultValue: "“{{prompt}}”",
                        prompt: selectedInstruction,
                      })}
                    </p>
                  </div>
                ) : null}
              </div>
              {canRestore && !isLatest && selectedVersion != null ? (
                <Button
                  size="sm"
                  iconStart="rotate-ccw"
                  onClick={handleRestore}
                  disabled={restore.isPending}
                >
                  {t("personaOpportunities.scripts.history.useThisVersion", "Use this version")}
                </Button>
              ) : null}
            </div>

            {leading ? leading(view, scenePreview) : scenePreview}
          </div>

          {rail.isHidden ? null : rail.isCollapsed ? (
            <CollapsedVersionsRail
              rail={rail}
              versions={timelineVersions}
              activeVersionId={activeVersionId}
              onSelect={selectVersion}
            />
          ) : (
          <div
            id={rail.id}
            className={cn("flex min-h-0 min-w-0 flex-col overflow-hidden", drawer && OPEN_RAIL_CLASS)}
          >
            <div
              className={cn(
                "flex shrink-0 items-start justify-between gap-2 pt-4 pb-2",
                drawer ? cn("px-3", OPEN_RAIL_CONTENT_CLASS) : "px-[18px]",
              )}
            >
              <div className="min-w-0">
                <p
                  className={
                    drawer
                      ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
                      : "font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                  }
                >
                  {t("personaOpportunities.scripts.history.versions", "Versions")}
                </p>
                <p className={drawer ? "mt-1 text-caption leading-snug text-muted-foreground" : "mt-1 text-[11px] leading-snug text-muted-foreground"}>
                  {t(
                    "personaOpportunities.scripts.history.selectHint",
                    "Select one to preview it",
                  )}
                </p>
              </div>
              {drawer ? <VersionsRailCollapseButton rail={rail} /> : null}
            </div>
            <div className={cn("min-h-0 flex-1 overflow-x-hidden overflow-y-auto", drawer ? cn("px-3 pb-3", OPEN_RAIL_CONTENT_CLASS) : "px-[18px] pb-[18px]")}>
              {historyQuery.isLoading ? (
                <div
                  className="flex flex-col gap-2"
                  aria-label={t(
                    "personaOpportunities.scripts.history.loadingVersions",
                    "Loading versions…",
                  )}
                >
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : historyQuery.isError ? (
                <div className="flex flex-col items-start gap-2">
                  <p className={drawer ? "text-drawer-body text-muted-foreground" : "text-sm text-muted-foreground"}>
                    {t(
                      "personaOpportunities.scripts.history.loadError",
                      "Couldn't load version history.",
                    )}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => void historyQuery.refetch()}>
                    {t("personaOpportunities.scripts.history.retry", "Retry")}
                  </Button>
                </div>
              ) : (
                <VersionTimeline
                  variant="rail"
                  versions={timelineVersions}
                  activeVersionId={activeVersionId}
                  onSelect={(entry) => selectVersion(entry.version)}
                  labels={{
                    latest: t("personaOpportunities.scripts.history.latest", "Latest"),
                    empty: t("personaOpportunities.scripts.history.empty", "No versions yet."),
                    select: t("personaOpportunities.scripts.history.select", "Select version"),
                  }}
                  activeFooter={
                    canShowChanges ? (
                      <label
                        className={cn(
                          "flex min-h-11 min-w-0 cursor-pointer flex-wrap items-center gap-2 text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:min-h-7",
                          drawer ? "text-caption" : "text-[11px]",
                        )}
                      >
                        <Switch
                          checked={isShowingChanges}
                          onCheckedChange={(checked) =>
                            setDiffVersion(checked ? viewingVersion : null)
                          }
                          aria-label={showDifferencesLabel}
                        />
                        <span>{showDifferencesLabel}</span>
                      </label>
                    ) : null
                  }
                />
              )}
            </div>
          </div>
          )}
        </div>
    </div>
  );
}

export function ScriptsHistoryDrawer({
  open,
  onOpenChange,
  setId,
  tenantId,
  script,
  canRestore,
}: ScriptsHistoryDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[1200px] flex-col gap-0 p-0 sm:max-w-[1200px]"
      >
        <ScriptsHistoryPanel
          enabled={open}
          setId={setId}
          tenantId={tenantId}
          script={script}
          canRestore={canRestore}
          header={(view) => <ScriptsHistorySheetHeader view={view} />}
        />
      </SheetContent>
    </Sheet>
  );
}

function ScriptsHistorySheetHeader({ view }: { view: ScriptHistoryView }) {
  const { t } = useTranslation();
  return (
    <SheetHeader className="shrink-0 gap-0 px-6 py-[18px]">
      <div className="flex min-w-0 items-center gap-2 pr-8">
        <SheetTitle className="truncate text-[15px] font-semibold">{view.title}</SheetTitle>
        <Badge variant="default" className="rounded-full px-2 py-px text-[11px]">
          {t("personaOpportunities.scripts.history.versionCount", {
            defaultValue_one: "{{count}} version",
            defaultValue_other: "{{count}} versions",
            count: view.versionCount,
          })}
        </Badge>
      </div>
      <SheetDescription className="mt-[3px] text-xs text-muted-foreground">
        {t("personaOpportunities.scripts.history.subtitle", "Version history")}
      </SheetDescription>
    </SheetHeader>
  );
}

function sceneFieldText(
  script: PersonaScriptDto | undefined,
  sceneNumber: number,
  field: PersonaScriptHighlightField,
): string {
  return script?.scenes.find((scene) => scene.sceneNumber === sceneNumber)?.[field] ?? "";
}

function previewDiffersFromPrevious(
  preview: PersonaScriptDto,
  previous: PersonaScriptDto,
): boolean {
  return preview.scenes.some((scene) =>
    SCENE_FIELDS.some(
      ({ field }) =>
        (scene[field] ?? "") !== sceneFieldText(previous, scene.sceneNumber, field),
    ),
  );
}

function HistoryPreview({
  surface,
  script,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  surface: ScriptHistorySurface;
  script: PersonaScriptDto;
  original: PersonaScriptDto | undefined;
  previous: PersonaScriptDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  if (surface === "drawer") {
    return (
      <HistorySceneTable
        script={script}
        original={original}
        previous={previous}
        blameByKey={blameByKey}
        onSelectVersion={onSelectVersion}
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {script.scenes.map((scene) => (
        <HistorySceneCard
          key={scene.sceneNumber}
          surface={surface}
          scene={scene}
          script={script}
          original={original}
          previous={previous}
          blameByKey={blameByKey}
          onSelectVersion={onSelectVersion}
        />
      ))}
    </div>
  );
}

function HistorySceneTable({
  script,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  script: PersonaScriptDto;
  original: PersonaScriptDto | undefined;
  previous: PersonaScriptDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  const { t } = useTranslation();
  const empty = t("personaOpportunities.scripts.history.emDash", "—");
  const { indexWidth, sceneWidth, sideWidth, pad, headClass, cellClass, frameClass, tableClass } =
    personaScriptSceneTableLayout("drawer");
  const cellTextClass = cn(
    pad,
    "py-3 align-top leading-relaxed break-words whitespace-pre-wrap",
    cellClass,
  );

  function fieldState(scene: PersonaScriptDto["scenes"][number], field: PersonaScriptHighlightField) {
    const isDifferent = personaScriptSceneFieldDiffersFromOriginal(
      script,
      original,
      scene.sceneNumber,
      field,
    );
    const blameVersion = isDifferent
      ? (blameByKey.get(sceneFieldBlameKey(scene.sceneNumber, field)) ?? null)
      : null;
    return { isDifferent, blameVersion };
  }

  function renderFieldBody(
    scene: PersonaScriptDto["scenes"][number],
    field: PersonaScriptHighlightField,
    blameVersion: number | null,
  ) {
    return (
      <>
        {blameVersion != null ? (
          <SceneFieldBlameButton
            surface="drawer"
            version={blameVersion}
            onSelectVersion={onSelectVersion}
            className="mb-1 block"
          />
        ) : null}
        <PersonaScriptFieldDiff
          before={previous ? sceneFieldText(previous, scene.sceneNumber, field) : undefined}
          after={scene[field] ?? ""}
          empty={empty}
        />
      </>
    );
  }

  return (
    <div
      className={cn(
        frameClass,
        "rounded-sm border border-border",
        PERSONA_SCRIPT_SCENE_SCROLL_FOCUS_CLASS,
      )}
      tabIndex={0}
      role="region"
      aria-label={t("personaOpportunities.scripts.sceneColumnsScroll", "Scene columns")}
    >
      <Table className={tableClass}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={cn(indexWidth, pad, headClass)}>
              {t("personaOpportunities.scripts.sceneNumberColumn", "#")}
            </TableHead>
            <TableHead className={cn(sceneWidth, pad, headClass)}>
              {t("personaOpportunities.scripts.sceneColumn", "Scene")}
            </TableHead>
            {SCENE_FIELDS.map(({ field, key, fallback }) => (
              <TableHead
                key={field}
                className={cn(
                  field === "product" || field === "props" ? sideWidth : undefined,
                  pad,
                  headClass,
                )}
              >
                {t(key, fallback)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {script.scenes.map((scene, index) => (
            <TableRow key={scene.sceneNumber} className={personaScriptSceneRowClass(index)}>
              <TableCell className={cn(pad, "py-3 align-top tabular-nums text-muted-foreground", cellClass)}>
                {scene.sceneNumber}
              </TableCell>
              <TableCell className={cn(pad, "py-3 align-top break-words")}>
                <span className="inline-block max-w-full break-words rounded-sm border border-border bg-background px-2 py-0.5 text-caption text-foreground">
                  {scene.sceneName}
                </span>
              </TableCell>
              {SCENE_FIELDS.map(({ field }) => {
                const { isDifferent, blameVersion } = fieldState(scene, field);
                return (
                  <TableCell
                    key={field}
                    className={cn(cellTextClass, isDifferent && highlightedSceneClass(true))}
                  >
                    {renderFieldBody(scene, field, blameVersion)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SceneFieldBlameButton({
  surface,
  version,
  onSelectVersion,
  className,
}: {
  surface: ScriptHistorySurface;
  version: number;
  onSelectVersion: (version: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onSelectVersion(version)}
      className={cn(
        surface === "drawer"
          ? "shrink-0 cursor-pointer text-caption font-medium tabular-nums text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          : "shrink-0 cursor-pointer font-mono text-[10px] font-medium tabular-nums tracking-normal text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        className,
      )}
      aria-label={t("personaOpportunities.scripts.history.changedInVersion", {
        defaultValue: "Changed in v{{version}}",
        version,
      })}
    >
      {t("personaOpportunities.scripts.history.versionShort", {
        defaultValue: "v{{version}}",
        version,
      })}
    </button>
  );
}

function HistorySceneCard({
  surface,
  scene,
  script,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  surface: ScriptHistorySurface;
  scene: PersonaScriptDto["scenes"][number];
  script: PersonaScriptDto;
  original: PersonaScriptDto | undefined;
  previous: PersonaScriptDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  const { t } = useTranslation();
  const empty = t("personaOpportunities.scripts.history.emDash", "—");
  const drawer = surface === "drawer";

  return (
    <article className="overflow-hidden rounded-sm border border-border">
      <header className="flex items-center gap-2 border-b border-border bg-muted px-3 py-[9px]">
        <span className={drawer ? "text-caption tabular-nums text-muted-foreground" : "text-[11px] tabular-nums tracking-[-0.01em] text-muted-foreground"}>
          {t("personaOpportunities.scripts.history.sceneNumber", {
            defaultValue: "{{number}}",
            number: scene.sceneNumber,
          })}
        </span>
        <span className={drawer ? "rounded-sm border border-border bg-background px-2 py-px text-caption text-foreground" : "rounded-sm border border-border bg-background px-2 py-px text-[11px] text-foreground"}>
          {scene.sceneName}
        </span>
      </header>
      <dl className="grid grid-cols-2">
        {SCENE_FIELDS.map(({ field, key, fallback }, index) => {
          const isDifferent = personaScriptSceneFieldDiffersFromOriginal(
            script,
            original,
            scene.sceneNumber,
            field,
          );
          const blameVersion = isDifferent
            ? (blameByKey.get(sceneFieldBlameKey(scene.sceneNumber, field)) ?? null)
            : null;

          return (
            <div
              key={field}
              className={cn(
                "px-3 py-[9px]",
                index % SCENE_GRID_COLUMNS === 0 && "border-r border-border",
                index < SCENE_GRID_COLUMNS && "border-b border-border",
                highlightedSceneClass(isDifferent),
              )}
            >
              <dt className="mb-1 flex items-center justify-between gap-2">
                <span
                  className={
                    drawer
                      ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
                      : "font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase"
                  }
                >
                  {t(key, fallback)}
                </span>
                {blameVersion != null ? (
                  <SceneFieldBlameButton
                    surface={surface}
                    version={blameVersion}
                    onSelectVersion={onSelectVersion}
                  />
                ) : null}
              </dt>
              <dd className={drawer ? "text-drawer-body whitespace-pre-wrap text-foreground" : "text-xs leading-[1.55] whitespace-pre-wrap text-foreground"}>
                <PersonaScriptFieldDiff
                  before={
                    previous
                      ? sceneFieldText(previous, scene.sceneNumber, field)
                      : undefined
                  }
                  after={scene[field] ?? ""}
                  empty={empty}
                />
              </dd>
            </div>
          );
        })}
      </dl>
    </article>
  );
}
